import {
  Box,
  Button,
  Icon,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import React from "react"
import { RetentionUploadRequest } from "types/request"
import { IIssuedDocument } from "types/response"

import { IRetentionInfo } from "types/retentionInfoType"
import {
  retentionLabels,
  RetentionValidationResult,
  SERIAL_REGEX,
  validateRetention,
} from "utils"

type Props = {
  retention: IRetentionInfo
  document: IIssuedDocument
  value: RetentionUploadRequest
  onChange: (value: RetentionUploadRequest) => void
  onValidationChange: (
    retKey: string,
    result: RetentionValidationResult,
  ) => void
}

const RetentionInfo = (props: Props) => {
  const { retention, document, value, onChange, onValidationChange } = props
  const bases = document.dataInvoice.bases
  const valorSugerido = bases[retention.retKey]
  const baseImponible = bases[retention.baseKey]
  const tolerance = document.tolerance
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [error, setError] = React.useState<boolean>(false)
  const [errorSerial, setErrorSerial] = React.useState<boolean>(false)
  const [displayValue, setDisplayValue] = React.useState<string>("")

  const retentionColors: Record<string, string> = {
    retIva75: "#f59e0b",
    retISLR2: "#d32f2f",
    retISLR3: "#546e7a",
  }

  const toleranciaValor = valorSugerido * (tolerance / 100)
  const minFormatted = (valorSugerido - toleranciaValor)
    .toFixed(2)
    .replace(".", ",")
  const maxFormatted = (valorSugerido + toleranciaValor)
    .toFixed(2)
    .replace(".", ",")

  const formatCurrency = (value: number | string): string => {
    return `VES ${new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value))}`
  }

  const reportValidation = (
    currentValue: RetentionUploadRequest,
    valueStr: string,
  ) => {
    const result = validateRetention({
      retainedValue: valueStr,
      serialNumber: currentValue.serialNumber,
      pdf: currentValue.pdf,
      valorSugerido,
      tolerance,
      label: retentionLabels[retention.retKey] ?? retention.retKey,
    })
    onValidationChange(retention.retKey, result)
  }

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    newValue = newValue.replace(".", ",")
    if (!/^\d*,?\d{0,2}$/.test(newValue)) return

    setDisplayValue(newValue)
    const normalized = newValue.replace(",", ".")
    const updated = { ...value, retainedValue: normalized }
    onChange(updated)

    if (newValue === "" || newValue === ",") {
      setError(false)
      reportValidation(updated, newValue)
      return
    }

    const numericValue = Number(normalized)
    const toleranciaValor = valorSugerido * (tolerance / 100)
    const min = valorSugerido - toleranciaValor
    const max = valorSugerido + toleranciaValor
    const hasError =
      isNaN(numericValue) || numericValue < min || numericValue > max

    setError(hasError)
    reportValidation(updated, newValue)
  }

  const handleChangeSerial = (e: React.ChangeEvent<HTMLInputElement>) => {
    const serial = e.target.value.toUpperCase().replace(/\s/g, "")

    if (serial.length > 14) {
      const updated = { ...value, serialNumber: "" }
      onChange(updated)
      setErrorSerial(true)
      reportValidation(updated, displayValue)
      return
    }

    const updated = { ...value, serialNumber: serial }
    onChange(updated)

    if (serial === "") {
      setErrorSerial(false)
      reportValidation(updated, displayValue)
      return
    }

    if (serial.length < 14) {
      setErrorSerial(true)
      reportValidation(updated, displayValue)
      return
    }

    // serial.length === 14, validar formato
    const match = serial.match(SERIAL_REGEX)
    if (!match) {
      setErrorSerial(true)
      reportValidation(updated, displayValue)
      return
    }

    const year = Number(match[1])
    const currentYear = new Date().getFullYear()
    const hasSerialError = year < currentYear - 100 || year > currentYear + 100
    setErrorSerial(hasSerialError)
    reportValidation(updated, displayValue)
  }

  const handleUploadPDF = (file: File | null) => {
    const updated = { ...value, pdf: file }
    onChange(updated)
    reportValidation(updated, displayValue)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  return (
    <Paper
      className="p-4 rounded-2xl shadow-md mt-2"
      style={{
        borderTop: `3px solid ${retentionColors[retention.retKey] ?? "#546e7a"}`,
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <Box
          style={{
            background: retentionColors[retention.retKey] ?? "#546e7a",
            color: "white",
            borderRadius: 8,
            padding: "3px 10px",
            fontWeight: 700,
            fontSize: 13,
            whiteSpace: "nowrap",
            minWidth: 160,
            textAlign: "center",
          }}
        >
          Retención {retention.retentionType} ({retention.percent})
        </Box>

        <Typography
          variant="body2"
          style={{ color: "#4A4443", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          Base imponible
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontWeight: "bold",
            color: "#1976d2",
            backgroundColor: "#e3f2fd",
            padding: "2px 6px",
            borderRadius: 4,
            whiteSpace: "nowrap",
          }}
        >
          {formatCurrency(baseImponible)}
        </Typography>

        <Typography
          variant="body2"
          style={{ color: "#4A4443", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          Valor sugerido
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontWeight: "bold",
            color: "#1976d2",
            backgroundColor: "#e3f2fd",
            padding: "2px 6px",
            borderRadius: 4,
            whiteSpace: "nowrap",
          }}
        >
          {formatCurrency(valorSugerido)}
        </Typography>
      </Box>

      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingLeft: 170,
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          label="Valor retenido"
          InputLabelProps={{ shrink: true, style: { fontSize: 13.5 } }}
          placeholder={`Ej: ${String(valorSugerido).replace(".", ",")}`}
          value={displayValue}
          onChange={handleChangeValue}
          style={{ width: 220 }}
          error={error}
          InputProps={{ style: { fontSize: 13 } }}
          inputProps={{ inputMode: "decimal" }}
          helperText={
            error ? `Debe estar entre ${minFormatted} y ${maxFormatted}` : " "
          }
          FormHelperTextProps={{
            style: { minHeight: 18, margin: "2px 0 0 0" },
          }}
        />

        <TextField
          size="small"
          variant="outlined"
          label="Serial"
          InputLabelProps={{ shrink: true, style: { fontSize: 14.5 } }}
          placeholder="Ej: 20251259448756"
          value={value.serialNumber || ""}
          onChange={handleChangeSerial}
          style={{ width: 220 }}
          error={errorSerial}
          InputProps={{ style: { fontSize: 13 } }}
          helperText={
            errorSerial ? "14 dígitos: año(4) + mes(2) + serial(8)." : " "
          }
          FormHelperTextProps={{
            style: { minHeight: 18, margin: "2px 0 0 0" },
          }}
        />

        <Box
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 6,
            paddingBottom: 21,
          }}
        >
          <Button
            variant="contained"
            component="label"
            startIcon={
              value.pdf ? <Icon>check</Icon> : <Icon>attach_file</Icon>
            }
            size="small"
            style={{
              height: 36,
              width: 200,
              minWidth: 200,
              maxWidth: 200,
              backgroundColor: "#455a64",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: "0.03em",
              justifyContent: "flex-start",
              paddingLeft: 8,
              color: "white",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 145,
                display: "block",
                color: "white",
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {value.pdf ? value.pdf.name.replace(".pdf", "") : "Carga un PDF"}
            </span>
            <input
              type="file"
              hidden
              accept="application/pdf"
              ref={fileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleUploadPDF(file)
              }}
            />
          </Button>

          {value.pdf && (
            <Tooltip title="Eliminar archivo PDF" placement="top">
              <IconButton
                size="small"
                onClick={() => handleUploadPDF(null)}
                style={{ color: "#d32121", padding: 4 }}
              >
                <Icon style={{ fontSize: 20 }}>delete</Icon>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

export default RetentionInfo
